import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  UserIcon, 
  TrophyIcon, 
  ShareIcon,
  CameraIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Postcard, Achievement } from '../types';

// Helper function to convert Firestore timestamp to Date
const convertToDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  if (timestamp.toDate) return timestamp.toDate();
  return new Date(timestamp);
};

export function ProfilePage() {
  const { currentUser, userProfile } = useAuth();
  const [userPostcards, setUserPostcards] = useState<Postcard[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, [currentUser]);

  const fetchUserData = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Fetch user's postcards
      const postcardsQuery = query(
        collection(db, 'postcards'),
        where('createdBy', '==', currentUser.uid)
      );
      const postcardsSnapshot = await getDocs(postcardsQuery);
      const postcardsData: Postcard[] = [];
      
      postcardsSnapshot.forEach((doc) => {
        const data = doc.data();
        postcardsData.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date()
        } as Postcard);
      });
      
      setUserPostcards(postcardsData);

      // Fetch achievements from Firestore
      const achievementsQuery = query(collection(db, 'achievements'));
      const achievementsSnapshot = await getDocs(achievementsQuery);
      
      const achievementsData: Achievement[] = [];
      
      for (const docSnapshot of achievementsSnapshot.docs) {
        const achievementData = docSnapshot.data();
        
        // Check if user has unlocked this achievement
        const unlockedAt = userProfile?.stats.achievements?.includes(docSnapshot.id) 
          ? new Date() // In a real app, you'd store the actual unlock date
          : undefined;
        
        achievementsData.push({
          id: docSnapshot.id,
          title: achievementData.title,
          description: achievementData.description,
          icon: achievementData.icon,
          points: achievementData.points,
          category: achievementData.category,
          unlockedAt,
          requirements: achievementData.requirements
        });
      }
      
      // If no achievements in database, use default achievements
      if (achievementsData.length === 0) {
        const defaultAchievements: Achievement[] = [
          {
            id: 'first-postcard',
            title: 'First Postcard',
            description: 'Send your first postcard',
            icon: '📮',
            points: 10,
            category: 'milestone',
            unlockedAt: (userProfile?.stats.postcardsSent || 0) >= 1 ? new Date() : undefined,
            requirements: {
              postcardsSent: 1
            }
          },
          {
            id: 'global-explorer',
            title: 'Global Explorer',
            description: 'Send postcards from 5 different countries',
            icon: '🌍',
            points: 250,
            category: 'exploration',
            unlockedAt: (userProfile?.stats.countriesReached || 0) >= 5 ? new Date() : undefined,
            requirements: {
              countriesReached: 5
            }
          },
          {
            id: 'social-butterfly',
            title: 'Social Butterfly',
            description: 'Have a high reply rate',
            icon: '🦋',
            points: 300,
            category: 'social',
            unlockedAt: (userProfile?.stats.replyRate || 0) >= 80 ? new Date() : undefined,
            requirements: {
              replyRate: 80
            }
          },
          {
            id: 'photographer',
            title: 'Photographer',
            description: 'Send 25 postcards with great photos',
            icon: '📸',
            points: 400,
            category: 'creative',
            unlockedAt: (userProfile?.stats.postcardsSent || 0) >= 25 ? new Date() : undefined,
            requirements: {
              postcardsSent: 25
            }
          }
        ];
        
        setAchievements(defaultAchievements);
      } else {
        setAchievements(achievementsData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const shareProfile = () => {
    const profileUrl = `${window.location.origin}/profile/${currentUser?.uid}`;
    if (navigator.share) {
      navigator.share({
        title: `${userProfile?.displayName}'s Travel Profile`,
        text: `Check out ${userProfile?.displayName}'s travel postcards on Touristic!`,
        url: profileUrl
      });
    } else {
      navigator.clipboard.writeText(profileUrl);
      alert('Profile URL copied to clipboard!');
    }
  };

  const unlockedAchievements = achievements.filter(a => a.unlockedAt);
  const totalPoints = unlockedAchievements.reduce((sum, a) => sum + a.points, 0);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl p-6 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Profile</h1>
              <p className="text-primary-100">
                Your travel journey and achievements
              </p>
            </div>
            <button
              onClick={shareProfile}
              className="flex items-center px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
            >
              <ShareIcon className="h-5 w-5 mr-2" />
              Share Profile
            </button>
          </div>
        </motion.div>
      </div>

      {/* User Info */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
            {userProfile?.photoURL ? (
              <img
                src={userProfile.photoURL}
                alt="Profile"
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <UserIcon className="h-10 w-10 text-white" />
            )}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{userProfile?.displayName}</h2>
            <p className="text-gray-600">{userProfile?.email}</p>
            <p className="text-sm text-gray-500">
              Member since {(() => {
                const date = convertToDate(userProfile?.createdAt);
                return date ? date.toLocaleDateString() : 'Unknown';
              })()}
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-primary-600">{userPostcards.length}</div>
          <div className="text-gray-600">Postcards Sent</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-secondary-600">{userProfile?.stats.postcardsReceived || 0}</div>
          <div className="text-gray-600">Postcards Received</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-accent-600">{userProfile?.stats.countriesReached || 0}</div>
          <div className="text-gray-600">Countries Reached</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-yellow-600">{totalPoints}</div>
          <div className="text-gray-600">Total Points</div>
        </div>
      </div>

      {/* Achievements */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <TrophyIcon className="h-5 w-5 mr-2 text-yellow-600" />
          Achievements ({unlockedAchievements.length}/{achievements.length})
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {achievements.map((achievement) => (
            <motion.div
              key={achievement.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg border-2 transition-all ${
                achievement.unlockedAt
                  ? 'border-yellow-500 bg-yellow-50'
                  : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">{achievement.icon}</span>
                <span className="text-sm font-medium text-gray-600">
                  {achievement.points} pts
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{achievement.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{achievement.description}</p>
              {achievement.unlockedAt && (
                <div className="flex items-center text-xs text-yellow-600">
                  <StarIcon className="h-3 w-3 mr-1" />
                  Unlocked {(() => {
                    const date = convertToDate(achievement.unlockedAt);
                    return date ? date.toLocaleDateString() : 'Unknown';
                  })()}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Postcards */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <CameraIcon className="h-5 w-5 mr-2 text-primary-600" />
          Recent Postcards
        </h2>
        
        {userPostcards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {userPostcards.slice(0, 6).map((postcard) => (
              <div key={postcard.id} className="border rounded-lg overflow-hidden">
                <img
                  src={postcard.imageURL}
                  alt="Postcard"
                  className="w-full h-32 object-cover"
                />
                <div className="p-3">
                  <p className="text-sm text-gray-900 font-medium line-clamp-2 mb-1">
                    {postcard.message}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>{postcard.location}</span>
                    <span>{(() => {
                      const date = convertToDate(postcard.createdAt);
                      return date ? date.toLocaleDateString() : 'Unknown';
                    })()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <CameraIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No postcards yet. Start sharing your travels!</p>
          </div>
        )}
      </div>

      {/* Social Stats */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <ShareIcon className="h-5 w-5 mr-2 text-secondary-600" />
          Social Activity
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-primary-50 rounded-lg">
            <div className="text-2xl font-bold text-primary-600">
              {userPostcards.filter(p => p.isPublic).length}
            </div>
            <div className="text-gray-600">Public Postcards</div>
          </div>
          <div className="text-center p-4 bg-secondary-50 rounded-lg">
            <div className="text-2xl font-bold text-secondary-600">
              {userPostcards.reduce((sum, p) => sum + p.likes, 0)}
            </div>
            <div className="text-gray-600">Total Likes</div>
          </div>
          <div className="text-center p-4 bg-accent-50 rounded-lg">
            <div className="text-2xl font-bold text-accent-600">
              {new Set(userPostcards.map(p => p.country)).size}
            </div>
            <div className="text-gray-600">Countries Shared</div>
          </div>
        </div>
      </div>
    </div>
  );
} 